export type SocialEmbedPlatform = "facebook" | "instagram" | "tiktok" | "twitter" | "x";
export type SocialEmbedKind = "post" | "video" | "reel";

export type SocialEmbed = {
  platform: SocialEmbedPlatform;
  kind: SocialEmbedKind;
  url: string;
  embedUrl: string;
  fallbackText?: string;
};

export const SOCIAL_EMBED_MIN_WIDTH = 200;
export const SOCIAL_EMBED_MAX_WIDTH = 920;
export const SOCIAL_EMBED_DEFAULT_WIDTH = 560;

export const SOCIAL_EMBED_ERROR =
  "Nem támogatott beágyazás. Használható: YouTube videó, Instagram poszt/reel, TikTok videó, Facebook poszt/videó/reel/permalink URL, X/Twitter poszt URL vagy hivatalos X/Twitter embed HTML.";

export const FACEBOOK_OFFICIAL_EMBED_HELP =
  "Ez a Facebook tartalom nem engedi a sima URL-es beágyazást. Használd a Facebook hivatalos embed kódját.";

function canonicalHttpUrl(input: string) {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    url.protocol = "https:";
    url.hash = "";
    return url;
  } catch {
    return null;
  }
}

function isInstagramHost(hostname: string) {
  return hostname === "instagram.com" || hostname === "www.instagram.com";
}

function isTikTokHost(hostname: string) {
  return hostname === "tiktok.com" || hostname === "www.tiktok.com" || hostname.endsWith(".tiktok.com");
}

function isFacebookHost(hostname: string) {
  return hostname === "facebook.com" || hostname === "www.facebook.com" || hostname === "m.facebook.com" || hostname === "fb.watch";
}

function isFacebookPluginHost(hostname: string) {
  return hostname === "facebook.com" || hostname === "www.facebook.com";
}

function isTwitterHost(hostname: string) {
  return hostname === "twitter.com" || hostname === "www.twitter.com" || hostname === "x.com" || hostname === "www.x.com";
}

function twitterProvider(hostname: string): "twitter" | "x" {
  return hostname === "x.com" || hostname === "www.x.com" ? "x" : "twitter";
}

function stripHtml(input: string) {
  return input
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function extractTwitterHtml(input: string) {
  if (!/<(?:blockquote|script|a)\b/i.test(input) && !/twitter-tweet/i.test(input)) return null;
  const withoutScripts = input.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ");
  const blockquote = withoutScripts.match(/<blockquote\b[^>]*class=["'][^"']*\btwitter-tweet\b[^"']*["'][^>]*>[\s\S]*?<\/blockquote>/i)?.[0] ?? withoutScripts;
  const hrefs = Array.from(blockquote.matchAll(/\bhref\s*=\s*["']([^"']+)["']/gi)).map((match) => match[1]);
  const tweetHref = hrefs.reverse().find((href) => {
    const url = canonicalHttpUrl(href);
    return Boolean(url && isTwitterHost(url.hostname) && /\/status(?:es)?\/\d+/i.test(url.pathname));
  });
  if (!tweetHref) return null;
  return {
    url: tweetHref,
    fallbackText: stripHtml(blockquote).slice(0, 500),
  };
}

function facebookPluginKind(pathname: string): SocialEmbedKind | null {
  const path = pathname.toLowerCase().replace(/\/+$/, "");
  if (path === "/plugins/video.php") return "video";
  if (path === "/plugins/post.php") return "post";
  return null;
}

function parseFacebookPlugin(url: URL): SocialEmbed | null {
  const pluginKind = facebookPluginKind(url.pathname);
  if (!pluginKind || !isFacebookPluginHost(url.hostname)) return null;
  const href = url.searchParams.get("href")?.trim() ?? "";
  const canonical = href ? canonicalHttpUrl(href) : null;
  if (href && (!canonical || !isFacebookHost(canonical.hostname))) return null;
  const kind = pluginKind === "video" && canonical?.pathname.toLowerCase().replace(/\/+$/, "").startsWith("/reel/") ? "reel" : pluginKind;
  return {
    platform: "facebook",
    kind,
    url: canonical?.toString() ?? url.toString(),
    embedUrl: url.toString(),
  };
}

function parseInstagram(url: URL): SocialEmbed | null {
  if (!isInstagramHost(url.hostname)) return null;
  const [kind, shortcode] = url.pathname.split("/").filter(Boolean);
  if ((kind !== "p" && kind !== "reel") || !shortcode) return null;
  const cleanUrl = `https://www.instagram.com/${kind}/${encodeURIComponent(shortcode)}/`;
  return {
    platform: "instagram",
    kind: kind === "reel" ? "reel" : "post",
    url: cleanUrl,
    embedUrl: `https://www.instagram.com/${kind}/${encodeURIComponent(shortcode)}/embed/`,
  };
}

function parseTikTok(url: URL): SocialEmbed | null {
  if (!isTikTokHost(url.hostname)) return null;
  const videoId = url.pathname.match(/\/video\/(\d+)/)?.[1];
  if (!videoId) return null;
  const cleanUrl = `https://www.tiktok.com${url.pathname}`;
  return {
    platform: "tiktok",
    kind: "video",
    url: cleanUrl,
    embedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
  };
}

function parseFacebook(url: URL): SocialEmbed | null {
  const pluginEmbed = parseFacebookPlugin(url);
  if (pluginEmbed) return pluginEmbed;
  if (!isFacebookHost(url.hostname)) return null;
  const path = url.pathname.toLowerCase().replace(/\/+$/, "");
  const reelId = path.match(/^\/reel\/(\d+)$/)?.[1];
  const isVideo = path.includes("/videos/") || path.includes("/watch/");
  const isPermalink = path === "/permalink.php" && Boolean(url.searchParams.get("story_fbid") && url.searchParams.get("id"));
  const isPost = path.includes("/posts/") || isPermalink || path.includes("/photos/");
  if (!reelId && !isVideo && !isPost) return null;
  const kind: SocialEmbedKind = reelId || isVideo ? (reelId ? "reel" : "video") : "post";
  const cleanUrl = reelId ? `https://www.facebook.com/reel/${encodeURIComponent(reelId)}` : url.toString();
  const pluginPath = kind === "video" || kind === "reel" ? "video.php" : "post.php";
  return {
    platform: "facebook",
    kind,
    url: cleanUrl,
    embedUrl: `https://www.facebook.com/plugins/${pluginPath}?href=${encodeURIComponent(cleanUrl)}&show_text=${kind === "post" ? "true" : "false"}&width=500`,
  };
}

function parseTwitter(url: URL, fallbackText?: string): SocialEmbed | null {
  if (!isTwitterHost(url.hostname)) return null;
  const match = url.pathname.match(/^\/([^/]+)\/status(?:es)?\/(\d+)/i);
  if (!match) return null;
  const username = match[1];
  if (!username || username.toLowerCase() === "i") return null;
  const statusId = match[2];
  const provider = twitterProvider(url.hostname);
  const canonicalHost = provider === "x" ? "x.com" : "twitter.com";
  const cleanUrl = `https://${canonicalHost}/${encodeURIComponent(username)}/status/${encodeURIComponent(statusId)}`;
  return {
    platform: provider,
    kind: "post",
    url: cleanUrl,
    embedUrl: cleanUrl,
    ...(fallbackText ? { fallbackText } : {}),
  };
}

export function parseSocialEmbedUrl(input: string): SocialEmbed | null {
  const htmlEmbed = extractTwitterHtml(input);
  const url = canonicalHttpUrl(htmlEmbed?.url ?? input);
  if (!url) return null;
  return parseInstagram(url) ?? parseTikTok(url) ?? parseFacebook(url) ?? parseTwitter(url, htmlEmbed?.fallbackText);
}

export function isFacebookPluginEmbedUrl(input: string) {
  const url = canonicalHttpUrl(input);
  return Boolean(url && isFacebookPluginHost(url.hostname) && facebookPluginKind(url.pathname));
}

export function isGeneratedFacebookEmbed(embed: Pick<SocialEmbed, "platform" | "url" | "embedUrl">) {
  if (embed.platform !== "facebook") return false;
  try {
    const embedUrl = new URL(embed.embedUrl);
    const href = embedUrl.searchParams.get("href");
    return Boolean(facebookPluginKind(embedUrl.pathname) && href && href === embed.url);
  } catch {
    return false;
  }
}

export function socialEmbedTitle(embed: Pick<SocialEmbed, "platform" | "kind">) {
  if (embed.platform === "x") return "X poszt";
  if (embed.platform === "twitter") return "Twitter poszt";
  if (embed.platform === "instagram") return embed.kind === "reel" ? "Instagram reel" : "Instagram poszt";
  if (embed.platform === "tiktok") return "TikTok videó";
  if (embed.kind === "reel") return "Facebook reel";
  return embed.kind === "video" ? "Facebook videó" : "Facebook poszt";
}

export function clampSocialEmbedWidth(input: number) {
  return Math.min(SOCIAL_EMBED_MAX_WIDTH, Math.max(SOCIAL_EMBED_MIN_WIDTH, Math.round(input)));
}

export function socialEmbedIframeSrc(embedUrl: string, width: number | null | undefined) {
  const safeWidth = typeof width === "number" && Number.isFinite(width) ? clampSocialEmbedWidth(width) : SOCIAL_EMBED_DEFAULT_WIDTH;
  try {
    const url = new URL(embedUrl);
    if (isFacebookPluginHost(url.hostname) && facebookPluginKind(url.pathname)) {
      url.searchParams.set("width", String(safeWidth));
      return url.toString();
    }
  } catch {
    return embedUrl;
  }
  return embedUrl;
}

function pluginAspectRatio(embedUrl: string) {
  try {
    const url = new URL(embedUrl);
    const width = Number(url.searchParams.get("width"));
    const height = Number(url.searchParams.get("height"));
    if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
      return `${Math.round(width)} / ${Math.round(height)}`;
    }
  } catch {
    return null;
  }
  return null;
}

export function socialEmbedAspectRatio(embed: Pick<SocialEmbed, "platform" | "kind" | "embedUrl">) {
  const explicitRatio = pluginAspectRatio(embed.embedUrl);
  if (explicitRatio) return explicitRatio;
  if (embed.platform === "x" || embed.platform === "twitter") return "1 / 1";
  if (embed.platform === "facebook") {
    if (embed.kind === "reel") return "9 / 16";
    if (embed.kind === "video") return "16 / 9";
    return "1 / 1.12";
  }
  if (embed.platform === "instagram") return embed.kind === "reel" ? "9 / 16" : "4 / 5";
  if (embed.platform === "tiktok") return "9 / 16";
  return "16 / 9";
}
